import Dictionary from './dictionary';
import Pluralize from './pluralize';

import ErrorHandler from '@module/error-handler';

import { TIME_SPANS } from '@app/constant/times';
import { UNLIMITED_TRANSFER_LIMIT } from '@app/constant/sizes';
import Assets from '@app/assets';

export default class I18n {
  static get locale() {
    return Dictionary.locale;
  }

  static t(key, params) {
    let result;
    try {
      result = Dictionary.lookup(key);
    } catch (e) {
      ErrorHandler.track(`I18n Error: ${e}. TranslationKey: ${key}`);
      return key || '';
    }

    // If we have a 'count' param, we'll probably want to pluralize our text
    if (params && typeof params.count !== 'undefined') {
      result = Pluralize(result, params.count);
    }

    // No params
    if (!params) {
      return result;
    }
    if (typeof result !== 'string') {
      result = '';
    }

    // Replace %{key} params with their values
    return result.replace(/%{(\w+)}/g, (_, key) => params[key]);
  }

  static megabytes(number) {
    return (
      this.number(number / 1048576, {
        precision: 1,
        delimiter: '',
      }) +
      ' ' +
      I18n.t('number.human.storage_units.units.mb')
    );
  }

  static gigabytes(number) {
    return (
      this.number(number / 1073741824, {
        precision: 1,
        delimiter: '',
      }) +
      ' ' +
      I18n.t('number.human.storage_units.units.gb')
    );
  }

  static size(number, options = {}) {
    if (typeof number !== 'number') {
      // The unlimited transfer limit is null, and we don't want to track it.
      // We are interested only in unexpected values for number.
      if (!options.allowNull || number !== UNLIMITED_TRANSFER_LIMIT) {
        ErrorHandler.track(
          `I18n.size called with ${number}, returning empty string instead`
        );
      }
      return '';
    }

    // Set defaults
    const format = I18n.t('number.human.storage_units.format') || '%n%u';
    // This app uses the kb binary unit of 1024 consistently
    // However recent MacOS and others convert 1kb to 1000 bytes because it's more "friendly"
    // Which is why file sizes may not match what your computer says
    const kb = 1024;
    let bytes = Math.max(0, number);
    let iterations = 0;
    let unit;

    options.format = options.format || format;
    options.delimiter = options.delimiter || '';

    // Iterate of the kilobytes
    while (bytes >= kb && iterations < 4) {
      bytes = bytes / kb;
      iterations++;
    }

    // Determine the locale unit type
    if (iterations === 0) {
      unit = I18n.t('number.human.storage_units.units.byte', { count: bytes });
      options.precision = 0;
    } else {
      unit = [null, 'kb', 'mb', 'gb', 'tb'][iterations];
      unit = I18n.t('number.human.storage_units.units.' + unit);
      if (bytes - Math.floor(bytes) === 0) {
        options.precision = 0;
      }
    }

    // Format the number according to the locale
    number = I18n.number(bytes, options);

    // Set the number string format
    return options.format.replace('%u', unit).replace('%n', number);
  }

  static number(number, options = {}) {
    // Determine locale number formatting
    const format = I18n.t('number.format') || {};

    // Set default options
    options.precision = 'precision' in options ? options.precision : 1;
    options.separator = options.separator || format.separator || '.';
    options.delimiter =
      'delimiter' in options ? options.delimiter : format.delimiter || '';

    const negative = number < 0;
    const string = Math.abs(number).toFixed(options.precision).toString();
    const parts = string.split('.');
    const buffer = [];
    let formattedNumber;
    number = parts[0];

    // Numbers are usually divided in parts of 3
    while (number.length > 0) {
      buffer.unshift(number.substr(Math.max(0, number.length - 3), 3));
      number = number.substr(0, number.length - 3);
    }

    // Format our number buffer
    formattedNumber = buffer.join(options.delimiter);

    // Set precision and the negative flag if needed
    if (options.precision > 0) {
      formattedNumber += options.separator + parts[1];
    }
    if (negative) {
      formattedNumber = '-' + formattedNumber;
    }
    return formattedNumber;
  }

  static date(date = new Date(), format = 'default') {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const dayNumber = date.getDay();

    const replaceRules = {
      '%-d': day, // Day, non-padded
      '%d': ('0' + day).slice(-2), // Day, padded
      '%m': ('0' + (month + 1)).slice(-2), // Month, padded
      '%Y': year, // Full year
      '%y': year.toString().substr(2, 2), // last two digits of year
      '%B': I18n.t('date.month_names.' + (month + 1)), // Full month name
      '%A': I18n.t('date.day_names.' + dayNumber), // Full day name
    };

    const re = new RegExp(Object.keys(replaceRules).join('|'), 'gi');

    let dateFormat;
    if (format === 'without_day' || format === 'with_day') {
      // Fallback on some date formats since they are missing in some translations.
      if (format === 'with_day') {
        dateFormat = '%A, %-d %B %Y';
      }
      if (format === 'without_day') {
        dateFormat = '%-d %B %Y';
      }
    } else {
      dateFormat = I18n.t('date.formats.' + format);

      // If the format doesn't exist, fallback to default
      if (format === dateFormat) {
        dateFormat = I18n.t('date.formats.default');
      }
    }

    return dateFormat.replace(re, (match) => replaceRules[match]);
  }

  static time(date = new Date()) {
    let hours = ('0' + date.getHours()).slice(-2);
    const mins = ('0' + date.getMinutes()).slice(-2);
    let suffix = '';

    // AM or PM?
    if (date.toLocaleTimeString().match(/AM|PM/)) {
      hours = date.getHours() % 12 || 12;
      suffix =
        ' ' + (date.getHours() >= 12 && date.getHours() < 24 ? 'PM' : 'AM');
    }

    // Now stick it together
    return `${hours}:${mins}${suffix}`;
  }

  static relativeTime(seconds) {
    // Did we receive a date object? In that case simply compare it with our current timestamp and get the difference (in seconds);
    if (seconds instanceof Date) {
      seconds = (new Date().getTime() - seconds.getTime()) / 1000;
    }

    const mapping = [
      [TIME_SPANS.DAYS_365_IN_SECONDS, 'over_x_years'],
      [TIME_SPANS.DAYS_28_IN_SECONDS, 'x_months'],
      [TIME_SPANS.DAYS_7_IN_SECONDS, 'x_weeks'],
      [TIME_SPANS.ONE_DAY_IN_SECONDS, 'x_days'],
      [TIME_SPANS.ONE_HOUR_IN_SECONDS, 'about_x_hours'],
      [TIME_SPANS.ONE_MINUTE_IN_SECONDS, 'x_minutes'],
      [1, 'x_seconds'],
      [0, 'less_than_x_seconds'],
    ];

    for (const unit of mapping) {
      if (seconds < unit[0]) {
        continue;
      }
      let count = Math.round(seconds / unit[0]);
      if (isNaN(count)) {
        count = 0;
      }
      return I18n.t('datetime.distance_in_words.' + unit[1], { count });
    }

    return null;
  }

  static image(path) {
    if (I18n.locale !== 'en') {
      path = `locales/${I18n.locale}/${path}`;
    }

    return Assets.lookup(path);
  }
}
